import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { getParamAsInt } from "../../../app/utils";
import { useAppSelector } from "../../../app/hooks";
import { isAdminRole, isHigherThanDistrictAdmin, UserRole } from "../../../app/userSlice";
import { Loading } from "../../../components/Loading";
import { ClassLinkOrCleverIconWithTooltip } from "../../../components/ClassLinkOrCleverIcon";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import { useGetUserInfoQuery } from "../usersApiSlice";
import UserInformationSection from "./UserInformationSection";
import AccountInfoCard from "./AccountInfoCard";
import UserCoursesSection from "./UserCoursesSection";
import UserSchoolsSection from "./UserSchoolsSection";
import UserDistrictsSection from "./UserDistrictsSection";

export default function EditUser() {
	const [queryParams] = useSearchParams();
	const userId = getParamAsInt(queryParams, "userId");

	const { config } = useAppSelector((state) => state.site);
	const { schools } = useAppSelector((state) => state.user);

	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");

	const getUserInfoResult = useGetUserInfoQuery(userId ?? skipToken);
	const userInfo = getUserInfoResult.data;

	const navigate = useNavigate();

	useEffect(() => {
		if (getUserInfoResult.isError) {
			console.log(getUserInfoResult.error);
			navigate("/users");
		}
	}, [getUserInfoResult.isError, getUserInfoResult.error]);

	const tooltipContent = userInfo?.cleverID
		? "This user has a Clever account"
		: userInfo?.classLinkID
			? "This user has a ClassLink account"
			: undefined;

	return (
		<section>
			<h1>Manage User</h1>
			<Loading loading={getUserInfoResult.isLoading} />
			{userInfo && userId && (
				<section>
					<div className="flex mt-6 gap-x-3 items-center">
						{(!!userInfo.classLinkID || !!userInfo.cleverID) &&
							!config?.isUtahComposeSite && (
								<ClassLinkOrCleverIconWithTooltip
									isClassLinkEntity={!!userInfo.classLinkID}
									isCleverEntity={!!userInfo.cleverID}
									iconClasses="size-10"
									tooltipId="outside-site-icon-tooltip"
									tooltipContent={tooltipContent}
								/>
							)}
						<h2>{userInfo.fullName}</h2>
					</div>
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Info}
						message={successMessage}
						onClear={() => setSuccessMessage("")}
					/>
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Error}
						message={errorMessage}
						showIcon={true}
					/>
					<hr className="my-6" />
					<div className="flex gap-x-12">
						<UserInformationSection
							userInfo={userInfo}
							setSuccessMessage={setSuccessMessage}
							setErrorMessage={setErrorMessage}
						/>
						<AccountInfoCard userInfo={userInfo} />
					</div>
					{userInfo.districts && isHigherThanDistrictAdmin(userInfo.editorsRole) && (
						<UserDistrictsSection
							districts={userInfo.districts}
							userId={userId}
							userName={userInfo.fullName}
							canAddOrRemoveDistrict={userInfo.role === UserRole.DistrictAdmin}
							editorsRole={userInfo.editorsRole}
						/>
					)}
					{userInfo.schools && (
						<UserSchoolsSection
							schools={userInfo.schools}
							showDistrictColumn={userInfo.showDistrictForSchools}
							userId={userId}
							userName={userInfo.fullName}
							showDisenrollmentMessage={!isAdminRole(userInfo.role)}
							canRemoveSchool={
								userInfo.role === UserRole.SchoolAdmin ||
								!isAdminRole(userInfo.role)
							}
							canAddSchool={
								(isHigherThanDistrictAdmin(userInfo.editorsRole) ||
									userInfo.editorsRole === UserRole.DistrictAdmin ||
									(userInfo.editorsRole === UserRole.SchoolAdmin &&
										(schools?.length ?? 0) > 1)) &&
								(userInfo.role === UserRole.SchoolAdmin ||
									!isAdminRole(userInfo.role))
							}
						/>
					)}
					{userInfo.courses && (
						<UserCoursesSection
							userId={userId}
							userName={userInfo.fullName}
							userRole={userInfo.role}
							courses={userInfo.courses}
							schools={userInfo.schools}
							districts={userInfo.districts}
							showSchoolColumn={userInfo.showSchoolForCourses}
							canAddCourse={
								userInfo.schools.length > 0 &&
								userInfo.schools.some((s) => s.active) &&
								!userInfo.disabled
							}
							canRemoveCourse={userInfo.courses.length > 0}
							setErrorMessage={setErrorMessage}
						/>
					)}
				</section>
			)}
		</section>
	);
}
